import React, { useState, useEffect } from 'react';
import programmeService from '../../services/programmeService';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from './LoadingSpinner';
import { getInstitutionKey, getInstitutionLogoSrc, getInstitutionShortLabel } from '../../utils/institutionAssets';
import {
    getClassificationBadgeDisplay,
    getRequirementFulfillmentDisplay,
} from '../../utils/jupasClassificationDisplay';
import JupasProgrammeSplitLayout from '../widgets/JupasProgrammeSplitLayout';

const FILTER_PANEL_LEFT_CLASS = 'min-w-0 overflow-hidden rounded-lg border border-edge bg-surface p-2 text-left sm:p-3';
const FILTER_PANEL_RIGHT_CLASS = 'min-w-0 overflow-hidden rounded-lg border border-edge bg-surface-muted/40 p-2 text-left shadow-sm sm:p-3';
const FILTER_HEADER_CLASS =
    'flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2';
const FILTER_ACTIONS_CLASS =
    'grid w-full min-w-0 grid-cols-2 gap-1.5 sm:flex sm:w-auto sm:shrink-0 sm:flex-wrap sm:gap-2';
const FILTER_ACTION_BUTTON_CLASS =
    'inline-flex h-6 min-w-0 items-center justify-center truncate rounded-full px-2 text-[10px] sm:h-7 sm:px-3 sm:text-xs';

const InstitutionFilter = ({
    selectedCollection,
    onInstitutionChange,
    selectedInstitutions = [],
    selectedRequirementFilters = [],
    onRequirementFiltersChange,
    selectedClassificationFilters = [],
    onClassificationFiltersChange,
}) => {
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { t, i18n } = useTranslation();

    // Prestige order for sorting
    const prestigeOrder = [
        'The University of Hong Kong', // HKU
        'The Chinese University of Hong Kong', // CUHK
        'The Hong Kong University of Science and Technology', // HKUST
        'City University of Hong Kong', // CityU
        'The Hong Kong Polytechnic University', // PolyU
        'Hong Kong Baptist University', // HKBU
        'Lingnan University', // LingU
        'Hong Kong Shue Yan University', // SYU
        'The Education University of Hong Kong', // EDUHK
        'Hong Kong Metropolitan University', // HKMU
        'The Hang Seng University of Hong Kong', // HSU
        'Saint Francis University', // SFU
        'Tung Wah College', // TWC
        'UOW College Hong Kong', // uowCHK
        'Hong Kong Chu Hai College', // HKCHU
        'Technological and Higher Education Institute of Hong Kong, Vocational Training Council' // THEI
    ];

    // Load institutions when collection changes
    useEffect(() => {
        if (selectedCollection) {
            loadInstitutions();
        }
    }, [selectedCollection]);

    const loadInstitutions = async () => {
        setLoading(true);
        setError(null);

        try {
            const result = await programmeService.getAvailableInstitutions(selectedCollection);
            if (result.success) {
                // Sort institutions by prestige order
                const sortedInstitutions = [...result.data].sort((a, b) => {
                    const indexA = prestigeOrder.indexOf(a);
                    const indexB = prestigeOrder.indexOf(b);
                    if (indexA === -1 && indexB === -1) return 0;
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    return indexA - indexB;
                });
                setInstitutions(sortedInstitutions);
            } else {
                setError(t('common.failedToLoadInstitutions'));
            }
        } catch (err) {
            setError(err?.status === 429
                ? t('common.retry.tooManyRequests')
                : t('common.errorLoadingInstitutions'));
        } finally {
            setLoading(false);
        }
    };

    const handleInstitutionToggle = (institution) => {
        const isSelected = selectedInstitutions.includes(institution);
        let newSelection;

        if (isSelected) {
            newSelection = selectedInstitutions.filter(inst => inst !== institution);
        } else {
            newSelection = [...selectedInstitutions, institution];
        }

        onInstitutionChange(newSelection);
    };

    const handleSelectAll = () => {
        onInstitutionChange(institutions);
    };

    const handleClearAll = () => {
        onInstitutionChange([]);
    };

    const toggleFilter = (value, selectedValues, onChange) => {
        onChange(selectedValues.includes(value)
            ? selectedValues.filter(item => item !== value)
            : [...selectedValues, value]);
    };

    const requirementOptions = [
        { value: 'P', type: 'PASS', label: t('jupa.requirementFulfillment.pass') },
        { value: 'C', type: 'CAUTION', label: t('jupa.requirementFulfillment.caution') },
        { value: 'W', type: 'WARNING', label: t('jupa.requirementFulfillment.warning') },
    ];
    const classificationOptions = [
        { value: 8, label: t('jupa.classificationBadges.goldenTicket') },
        { value: 5, label: t('jupa.classificationBadges.safe') },
        { value: 2, label: t('jupa.classificationBadges.veryRisky') },
        { value: 7, label: t('jupa.classificationBadges.secure') },
        { value: 4, label: t('jupa.classificationBadges.moderate') },
        { value: 1, label: t('jupa.classificationBadges.dangerous') },
        { value: 6, label: t('jupa.classificationBadges.verySafe') },
        { value: 3, label: t('jupa.classificationBadges.risky') },
        { value: 0, label: t('jupa.classificationBadges.missionImpossible') },
    ];
    const showAdvancedFilters = typeof onRequirementFiltersChange === 'function'
        && typeof onClassificationFiltersChange === 'function';
    const institutionPillClass = (selected) => `grid h-7 min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center rounded-full border px-1 text-[10px] font-medium leading-none transition-colors sm:h-8 sm:grid-cols-[1.5rem_minmax(0,1fr)] sm:text-xs ${selected
        ? 'border-[#A0C4FF] bg-[#A0C4FF]/35 text-ink'
        : 'border-edge-strong bg-transparent text-ink-muted hover:bg-surface-muted'}`;

    if (loading) {
        const loadingPanel = (
            <div className={FILTER_PANEL_LEFT_CLASS}>
                <div className="flex items-center space-x-2">
                    <LoadingSpinner size="small" />
                    <span className="text-sm text-ink-muted">{t('common.loadingInstitutions')}</span>
                </div>
            </div>
        );

        if (!showAdvancedFilters) {
            return loadingPanel;
        }

        return (
            <JupasProgrammeSplitLayout
                compact
                list={loadingPanel}
                detail={<div className={FILTER_PANEL_RIGHT_CLASS} aria-hidden />}
            />
        );
    }

    if (error) {
        const errorPanel = (
            <div className={FILTER_PANEL_LEFT_CLASS}>
                <p className="text-sm text-red-600">{error}</p>
                <button
                    onClick={loadInstitutions}
                    className="mt-1 rounded bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary-hover"
                >
                    {t('jupa.retry')}
                </button>
            </div>
        );

        if (!showAdvancedFilters) {
            return errorPanel;
        }

        return (
            <JupasProgrammeSplitLayout
                compact
                list={errorPanel}
                detail={<div className={FILTER_PANEL_RIGHT_CLASS} aria-hidden />}
            />
        );
    }

    const institutionPanel = (
        <div className={FILTER_PANEL_LEFT_CLASS}>
            <div className="space-y-2">
                <div className={FILTER_HEADER_CLASS}>
                    <h4 className="flex min-w-0 items-center text-xs font-medium text-ink-muted sm:text-sm">{t('trajectory.filterByInstitution')}</h4>
                    <div className={FILTER_ACTIONS_CLASS}>
                        <button
                            onClick={handleSelectAll}
                            className={`${FILTER_ACTION_BUTTON_CLASS} bg-primary text-primary-foreground hover:bg-primary-hover`}
                        >
                            {t('common.selectAll')}
                        </button>
                        <button
                            onClick={handleClearAll}
                            className={`${FILTER_ACTION_BUTTON_CLASS} border border-edge bg-surface text-ink hover:bg-surface-muted`}
                        >
                            {t('common.clearAll')}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 sm:gap-2">
                    {institutions.map((institution) => {
                        const isSelected = selectedInstitutions.includes(institution);
                        const logoSrc = getInstitutionLogoSrc(institution);
                        const label = getInstitutionShortLabel(institution, i18n.language);
                        const fullLabel = t(`jupa.institutions.${getInstitutionKey(institution)}`, { defaultValue: institution });
                        return (
                            <button
                                type="button"
                                key={institution}
                                onClick={() => handleInstitutionToggle(institution)}
                                className={institutionPillClass(isSelected)}
                                aria-pressed={isSelected}
                                title={fullLabel}
                            >
                                {logoSrc ? (
                                    <span className="inline-flex h-5 w-5 items-center justify-center self-center rounded-full border border-gray-200 bg-white shadow-sm sm:h-6 sm:w-6">
                                        <img
                                            src={logoSrc}
                                            alt=""
                                            className="h-3.5 w-3.5 object-contain object-center sm:h-4 sm:w-4"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </span>
                                ) : null}
                                <span className="min-w-0 truncate text-center">{label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const classificationPanel = showAdvancedFilters ? (
        <div className={FILTER_PANEL_RIGHT_CLASS}>
            <div className="space-y-3">
                <div className={FILTER_HEADER_CLASS}>
                    <h4 className="text-xs font-medium text-ink-muted sm:text-sm">
                        {t('common.filter')}
                    </h4>
                    <div className={FILTER_ACTIONS_CLASS}>
                        <button
                            type="button"
                            onClick={() => {
                                onRequirementFiltersChange(requirementOptions.map(option => option.value));
                                onClassificationFiltersChange(classificationOptions.map(option => option.value));
                            }}
                            className={`${FILTER_ACTION_BUTTON_CLASS} bg-primary text-primary-foreground hover:bg-primary-hover`}
                        >
                            {t('common.selectAll')}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                onRequirementFiltersChange([]);
                                onClassificationFiltersChange([]);
                            }}
                            className={`${FILTER_ACTION_BUTTON_CLASS} border border-edge bg-surface text-ink hover:bg-surface-muted`}
                        >
                            {t('common.clearAll')}
                        </button>
                    </div>
                </div>
                <div className="min-w-0 space-y-1.5">
                    <h5 className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-subtle sm:text-xs">
                        {t('jupa.filterByRequirement')}
                    </h5>
                    <div className="grid min-w-0 grid-cols-3 gap-1 sm:gap-2">
                        {requirementOptions.map(option => {
                            const selected = selectedRequirementFilters.includes(option.value);
                            return (
                                <button
                                    type="button"
                                    key={option.value}
                                    onClick={() => toggleFilter(option.value, selectedRequirementFilters, onRequirementFiltersChange)}
                                    className={`inline-flex h-7 w-full min-w-0 items-center justify-center gap-0.5 rounded-full px-0.5 text-[9px] font-medium transition-all sm:h-8 sm:gap-1.5 sm:px-1.5 sm:text-xs ${
                                        selected
                                            ? 'bg-surface text-ink'
                                            : 'bg-transparent text-ink-muted hover:bg-surface'
                                    }`}
                                    aria-pressed={selected}
                                    title={option.label}
                                >
                                    <span className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold transition-colors sm:h-6 sm:w-6 sm:text-[11px] ${selected ? getRequirementFulfillmentDisplay({ classification: option.type }, t).className : 'bg-surface-muted'}`}>
                                        {option.value}
                                    </span>
                                    <span className="min-w-0 truncate">{option.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="min-w-0 space-y-1.5">
                    <h5 className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-subtle sm:text-xs">
                        {t('jupa.filterByClassification')}
                    </h5>
                    <div className="grid min-w-0 grid-cols-3 gap-1 sm:gap-2">
                        {classificationOptions.map(option => (
                            <button
                                type="button"
                                key={option.value}
                                onClick={() => toggleFilter(option.value, selectedClassificationFilters, onClassificationFiltersChange)}
                                className={`jupas-classification-filter-button inline-flex h-7 w-full min-w-0 items-center justify-center rounded-full border px-0.5 text-[9px] font-medium leading-none transition-all sm:h-8 sm:px-1.5 sm:text-xs ${
                                    selectedClassificationFilters.includes(option.value)
                                        ? `${getClassificationBadgeDisplay(option.value, t).className} border-transparent shadow-sm`
                                        : 'border-edge bg-surface/60 text-ink-muted hover:border-edge-strong hover:bg-surface'
                                }`}
                                aria-pressed={selectedClassificationFilters.includes(option.value)}
                                title={option.label}
                            >
                                <span className="min-w-0 truncate">{option.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    ) : null;

    if (!showAdvancedFilters) {
        return institutionPanel;
    }

    return (
        <JupasProgrammeSplitLayout
            compact
            list={institutionPanel}
            detail={classificationPanel}
        />
    );
};

export default InstitutionFilter; 
